import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { throwError } from 'rxjs/internal/observable/throwError';
import { filter } from 'rxjs/internal/operators/filter';
import { take } from 'rxjs/internal/operators/take';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { catchError } from 'rxjs/internal/operators/catchError';

import { AuthService } from '../services/auth.service';

// Auth endpoints must go through with whatever token is present (or none), or
// the re-login / token-refresh calls would themselves be intercepted.
const AUTH_ENDPOINTS = [
  '/api/users/login',
  '/api/users/templogin',
  '/api/users/refresh-token',
  '/api/users/revoke-refresh-token',
  '/api/auth/'
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    // Single-flight refresh: when a token has expired and many requests fire at
    // once, only the first triggers a refresh; the rest wait on this subject and
    // then replay with the new token.
    private isRefreshing = false;
    private refreshedToken$ = new BehaviorSubject<string>(null);

    constructor(
      private _authService: AuthService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (AUTH_ENDPOINTS.some(url => request.url.includes(url))) {
          return next.handle(request);
        }

        const token = this._authService.getToken();

        // Valid token → attach and go.
        if (token && !this._authService.isTokenExpired(token)) {
          return next.handle(this.withAuth(request, token));
        }

        // Token missing/expired but we can silently renew it → refresh, then go.
        // This is what stops the nightly midnight-expiry boot: instead of the
        // request 401ing and the error interceptor logging the user out, we get
        // a fresh access token behind the scenes.
        if (this._authService.getRefreshToken()) {
          return this.handleRefresh(request, next);
        }

        // Expired token with no way to renew → boot to login up front instead of
        // firing a doomed request.
        if (token) {
          this._authService.handleExpiredToken();
        }

        return next.handle(request);
    }


    private withAuth(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }


    private handleRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // A refresh is already in flight — wait for it, then replay this request.
        if (this.isRefreshing) {
          return this.refreshedToken$.pipe(
            filter(t => t != null),
            take(1),
            switchMap(newToken => next.handle(this.withAuth(request, newToken)))
          );
        }

        this.isRefreshing = true;
        this.refreshedToken$.next(null);

        return this._authService.refreshAccessToken().pipe(
          switchMap(newToken => {
            this.isRefreshing = false;
            this.refreshedToken$.next(newToken);
            return next.handle(this.withAuth(request, newToken));
          }),
          catchError(err => {
            this.isRefreshing = false;
            // Refresh token is invalid/expired — full logout to login.
            this._authService.handleExpiredToken();
            return throwError(() => err);
          })
        );
    }
}
